import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Paperclip, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Lets a parent attach a payment screenshot/receipt to an invoice reference. */
export const PaymentProofUpload = ({ reference }) => {
  const [count, setCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/media/payment-proof/${encodeURIComponent(reference)}`);
        setCount((res.data?.files || []).length);
      } catch (err) {
        setCount(0);
      }
    };
    if (reference) load();
  }, [reference]);

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("reference", reference);
    setUploading(true);
    try {
      await axios.post(`${API}/media/payment-proof`, form);
      setCount((c) => c + 1);
      toast.success("Proof of payment attached — the academy will verify it shortly");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not attach the file");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mt-2 flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`payment-proof-input-${reference}`}
      />
      <Button
        size="sm"
        variant="outline"
        className="text-xs border-slate-600"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`payment-proof-upload-${reference}`}
      >
        {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Paperclip className="w-3 h-3 mr-1" />}
        Attach payment proof
      </Button>
      {count > 0 && (
        <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid={`payment-proof-count-${reference}`}>
          <CheckCircle2 className="w-3 h-3" /> {count} attached
        </span>
      )}
    </div>
  );
};

export default PaymentProofUpload;
